import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useBilling } from '../contexts/BillingContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import PaymentForm from './PaymentForm';
import RazorpayPaymentForm from './RazorpayPaymentForm';
import { calculatePlanPriceSync, calculatePlanAnnualPriceSync } from '../config/stripeConfig';
import { getProMonthlyEmailLimit } from '../config/proEmailLimits';
import { workspaceService } from '../services/workspaceService';
import { subscriptionService } from '../services/subscriptionService';
import { paymentService } from '../services/paymentService';
import { referralService } from '../services/referralService';
import { Timestamp } from 'firebase/firestore';
import { userService } from '../services/firestore';
import {
  detectUserCountry,
  convertCurrency,
  formatCurrency,
  getCurrencyInfo,
  EXCHANGE_RATES,
} from '../utils/currencyDetection';
import { getAvailablePaymentGateways, isRazorpayCheckoutSuppressed } from '../utils/paymentGatewayConfig';
import { createDodoCheckoutSession } from '../services/dodoPaymentService';

interface CheckoutData {
  plan: 'pro' | 'business';
  subscribers: number;
  currency: string;
  billingPeriod: 'monthly' | 'annual';
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutData: CheckoutData | null;
  onSuccess?: () => void;
  /** When true (e.g. upgrade flow), skip plan recap — start at billing, 2-step UI only. */
  skipPlanSummary?: boolean;
}

type Step = 'summary' | 'billing' | 'payment';

interface BillingFormData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phoneNumber: string;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  checkoutData,
  onSuccess,
  skipPlanSummary = false,
}) => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { updateSubscription, refreshBillingInfo, preferShopifyBilling, initiateShopifyBilling } = useBilling();
  const { currentWorkspace, loadWorkspaces } = useWorkspace();

  const [currentStep, setCurrentStep] = useState<Step>(() =>
    skipPlanSummary ? 'billing' : 'summary'
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'upi' | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<'razorpay' | 'stripe' | 'dodo' | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCurrency, setUserCurrency] = useState<string>('USD');
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const [upiId, setUpiId] = useState('');
  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [shopifyBillingBusy, setShopifyBillingBusy] = useState(false);
  const [billingData, setBillingData] = useState<BillingFormData>({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phoneNumber: '',
  });

  // Detect user country and set currency
  useEffect(() => {
    const detectCountry = async () => {
      setIsDetectingCountry(true);
      try {
        const country = await detectUserCountry();
        setUserCountry(country);

        const isIndia = country === 'India' || country === 'IN';
        setUserCurrency(isIndia ? 'INR' : 'USD');
        const availableGateways = getAvailablePaymentGateways(country || null);
        setSelectedGateway(availableGateways[0] || null);
      } catch (error) {
        console.error('Error detecting country:', error);
        setUserCurrency('USD');
        const availableGateways = getAvailablePaymentGateways(null);
        setSelectedGateway(availableGateways[0] || null);
      } finally {
        setIsDetectingCountry(false);
      }
    };

    if (isOpen) {
      detectCountry();
    }
  }, [isOpen]);

  // Auto-select gateway when only one is available
  useEffect(() => {
    if (currentStep === 'payment' && !isDetectingCountry && !selectedGateway) {
      const availableGateways = getAvailablePaymentGateways(userCountry || billingData.country);
      if (availableGateways.length === 1) {
        setSelectedGateway(availableGateways[0] as 'razorpay' | 'stripe' | 'dodo');
      }
    }
  }, [currentStep, isDetectingCountry, selectedGateway, userCountry, billingData.country]);

  // When Dodo checkout is on, never keep Razorpay/Stripe selected (stale state or card pre-selected).
  useEffect(() => {
    if (!isOpen || !isRazorpayCheckoutSuppressed()) return;
    if (selectedGateway !== 'dodo') {
      setSelectedGateway('dodo');
      setSelectedPaymentMethod(null);
    }
  }, [isOpen, selectedGateway]);

  // Reset state when modal opens/closes
  useEffect(() => {
    const entryStep: Step = skipPlanSummary ? 'billing' : 'summary';
    if (isOpen && checkoutData) {
      setCurrentStep(entryStep);
      setIsProcessing(false);
      setSelectedPaymentMethod(null);
      setUpiId('');
      // Load user data when modal opens
      if (currentUser) {
        loadUserProfile();
        loadCustomer();
        loadReferralCredits();
      }
    } else if (!isOpen) {
      setCurrentStep(entryStep);
      setIsProcessing(false);
      setSelectedPaymentMethod(null);
      setUpiId('');
      setSelectedGateway(null);
      setAvailableCredits(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, checkoutData, currentUser, skipPlanSummary]);

  // Load user profile data
  const loadUserProfile = async () => {
    if (!currentUser) return;

    try {
      const profile = await userService.getUserProfile(currentUser.uid);
      if (profile) {
        setBillingData(prev => ({
          ...prev,
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || currentUser.email || '',
          companyName: profile.companyName || '',
          phoneNumber: profile.phoneNumber || '',
        }));
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  // Get or create Stripe customer (only needed for Stripe, not Razorpay)
  const loadCustomer = async () => {
    if (!currentUser || loadingCustomer) return;

    // Customer creation needed only for Stripe.
    if (selectedGateway !== 'stripe') {
      setLoadingCustomer(false);
      return;
    }

    setLoadingCustomer(true);
    try {
      const subscription = await subscriptionService.getUserSubscription(currentUser.uid);
      if (subscription?.stripeCustomerId) {
        setCustomerId(subscription.stripeCustomerId);
        setLoadingCustomer(false);
        return;
      }

      const profile = await userService.getUserProfile(currentUser.uid);
      if (!profile) {
        throw new Error('User profile not found');
      }

      const customer = await paymentService.createCustomer(
        profile.email || currentUser.email || '',
        profile.firstName && profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.displayName || undefined,
        currentUser.uid
      );

      setCustomerId(customer.id);
    } catch (error) {
      console.error('Error loading customer:', error);
      showError('Failed to initialize payment. Please try again.');
    } finally {
      setLoadingCustomer(false);
    }
  };

  // Load referral credits
  const loadReferralCredits = async () => {
    if (!currentUser) return;

    try {
      const stats = await referralService.getReferralStats(currentUser.uid);
      setAvailableCredits(stats.credits);
    } catch (error) {
      console.error('Error loading referral credits:', error);
      setAvailableCredits(0);
    }
  };

  // Calculate pricing in USD (base currency)
  const monthlyPriceUSD = checkoutData
    ? calculatePlanPriceSync(checkoutData.plan, checkoutData.subscribers)
    : 0;

  const annualPriceUSD = checkoutData
    ? calculatePlanAnnualPriceSync(checkoutData.plan, checkoutData.subscribers)
    : 0;

  const totalAmountUSD = checkoutData?.billingPeriod === 'annual' ? annualPriceUSD : monthlyPriceUSD;
  const savingsUSD = checkoutData?.billingPeriod === 'annual' ? monthlyPriceUSD * 12 - annualPriceUSD : 0;

  // Get detected country for regional pricing (check both userCountry and billingData.country)
  const detectedCountryForPricing = userCountry || billingData.country;

  const availableGatewaysForLabel = getAvailablePaymentGateways(detectedCountryForPricing);
  const checkoutGatewayLabel =
    availableGatewaysForLabel.length === 1
      ? availableGatewaysForLabel[0] === 'dodo'
        ? 'Dodo Payments'
        : availableGatewaysForLabel[0] === 'razorpay'
          ? 'Razorpay'
          : 'Stripe'
      : selectedGateway === 'dodo'
        ? 'Dodo Payments'
        : selectedGateway === 'razorpay'
          ? 'Razorpay'
          : selectedGateway === 'stripe'
            ? 'Stripe'
            : availableGatewaysForLabel.length === 0
              ? 'No checkout provider in this app version'
              : 'Choose on payment step';

  const displayCurrency = userCurrency === 'INR' ? 'INR' : 'USD';
  const fxRate = displayCurrency === 'INR' ? (EXCHANGE_RATES.INR || 83) : 1;
  const monthlyPrice = monthlyPriceUSD * fxRate;
  const annualPrice = (checkoutData?.billingPeriod === 'annual' ? annualPriceUSD : monthlyPriceUSD * 12) * fxRate;
  const totalAmount = totalAmountUSD * fxRate;
  const savings = savingsUSD * fxRate;

  // Calculate subscription dates
  const subscriptionStartDate = new Date();
  const subscriptionEndDate = new Date();

  if (checkoutData?.billingPeriod === 'annual') {
    subscriptionEndDate.setFullYear(subscriptionEndDate.getFullYear() + 1);
  } else {
    subscriptionEndDate.setMonth(subscriptionEndDate.getMonth() + 1);
  }

  // Calculate referral credits to apply (in USD). Dodo hosted checkout uses fixed catalog prices — credits are not applied there.
  const creditsToApplyUSD =
    selectedGateway === 'dodo' ? 0 : Math.min(availableCredits, totalAmountUSD);
  const creditsToApply = convertCurrency(creditsToApplyUSD, displayCurrency, userCountry);

  // Calculate amount after credits (before tax)
  const subtotalBeforeCredits = totalAmount;
  const subtotalAfterCredits = Math.max(0, subtotalBeforeCredits - creditsToApply);
  const finalAmountAfterCredits = subtotalAfterCredits;

  // Calculate tax (varies by country - 18% GST for India, 8% for others)
  const taxRate = userCountry === 'India' || userCountry === 'IN' ? 0.18 : 0.08;
  const subtotal = finalAmountAfterCredits;
  const tax = subtotal * taxRate;
  const grandTotal = subtotal + tax;

  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  const billingCycle = checkoutData?.billingPeriod === 'annual' ? 'Yearly' : 'Monthly';

  // Handle billing form changes
  const handleBillingChange = (field: keyof BillingFormData, value: string) => {
    setBillingData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Update currency when country changes
    if (field === 'country') {
      const isIndia = value === 'India' || value === 'IN';
      const availableGateways = getAvailablePaymentGateways(value);
      const gateway = isRazorpayCheckoutSuppressed() ? 'dodo' : availableGateways[0] || null;
      setUserCurrency(isIndia ? 'INR' : 'USD');
      setUserCountry(value);
      setSelectedGateway(gateway);
    }
  };

  // Check if all billing fields are filled (without showing errors)
  const isBillingInfoComplete = (): boolean => {
    return !!(
      billingData.firstName.trim() &&
      billingData.lastName.trim() &&
      billingData.email.trim() &&
      billingData.email.includes('@') &&
      billingData.addressLine1.trim() &&
      billingData.city.trim() &&
      billingData.state.trim() &&
      billingData.zipCode.trim() &&
      billingData.country.trim()
    );
  };

  // Validate billing information
  const validateBillingInfo = (): boolean => {
    if (!billingData.firstName.trim()) {
      showError('First name is required');
      return false;
    }
    if (!billingData.lastName.trim()) {
      showError('Last name is required');
      return false;
    }
    if (!billingData.email.trim() || !billingData.email.includes('@')) {
      showError('Valid email is required');
      return false;
    }
    if (!billingData.addressLine1.trim()) {
      showError('Address is required');
      return false;
    }
    if (!billingData.city.trim()) {
      showError('City is required');
      return false;
    }
    if (!billingData.state.trim()) {
      showError('State is required');
      return false;
    }
    if (!billingData.zipCode.trim()) {
      showError('ZIP code is required');
      return false;
    }
    if (!billingData.country.trim()) {
      showError('Country is required');
      return false;
    }
    return true;
  };

  // Create workspace if needed
  const createWorkspaceIfNeeded = async () => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const profile = await userService.getUserProfile(currentUser.uid);

      if (profile?.workspaceCompleted) {
        return;
      }

      const workspaceName = profile?.workspaceName || `${profile?.firstName || 'User'}'s Workspace`;
      const domain = profile?.domain || '';

      await workspaceService.createWorkspace(currentUser.uid, {
        name: workspaceName.trim(),
        description: `Workspace for ${profile?.firstName || 'User'}`,
        timezone: 'UTC',
        currency: 'usd',
        language: 'en',
        primaryColor: '#3b82f6',
        secondaryColor: '#1e40af'
      });

      await userService.updateUserProfile(currentUser.uid, {
        workspaceName: workspaceName.trim(),
        domain: domain.trim(),
        workspaceCompleted: true,
        workspaceCompletedAt: Timestamp.now()
      });

      await loadWorkspaces();
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  };

  // Handle payment success (for Stripe)
  const handlePaymentSuccess = async (paymentIntent: any) => {
    if (!checkoutData || !currentUser) return;

    setIsProcessing(true);

    try {
      await createWorkspaceIfNeeded();

      // Apply referral credits if any
      if (creditsToApplyUSD > 0) {
        await referralService.applyReferralCredits(currentUser.uid, totalAmountUSD);
      }

      await updateSubscription(
        checkoutData.plan,
        checkoutData.subscribers,
        displayCurrency.toLowerCase(),
        checkoutData.billingPeriod || 'monthly',
        grandTotal, // Pass actual payment amount for email
        tax, // Tax amount
        taxRate, // Tax rate (0.18 for India, 0.08 for others)
        paymentIntent.id, // Stripe Payment Intent ID
        'stripe'
      );

      // Award referral credit to the person who referred this user (if applicable)
      try {
        await referralService.awardReferralCredit(currentUser.uid);
      } catch (error) {
        console.error('Error awarding referral credit:', error);
        // Don't fail the subscription if referral credit fails
      }

      // Show success message
      const planName = checkoutData.plan === 'pro' ? 'Pro' : 'Business';
      showSuccess(
        `🎉 Payment successful! Your ${planName} plan subscription has been activated. You now have access to all premium features.`
      );

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close the modal after showing success message
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error creating subscription after payment:', error);
      showError(`Failed to create subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle Razorpay payment success
  const handleRazorpaySuccess = async (response: any) => {
    if (!checkoutData || !currentUser) return;

    setIsProcessing(true);

    try {
      await createWorkspaceIfNeeded();

      // SECURITY: the plan was already activated server-side by verifyRazorpayPayment
      // (server-priced order bound to this account). Just reload billing state.
      await refreshBillingInfo();

      // Award referral credit to the person who referred this user (if applicable)
      try {
        await referralService.awardReferralCredit(currentUser.uid);
      } catch (error) {
        console.error('Error awarding referral credit:', error);
        // Don't fail the subscription if referral credit fails
      }

      // Show success message with plan details
      const planName = checkoutData.plan === 'pro' ? 'Pro' : 'Business';
      showSuccess(
        `🎉 Payment successful! Your ${planName} plan subscription has been activated. You now have access to all premium features.`
      );

      // Call the onSuccess callback if provided (parent component can handle navigation)
      if (onSuccess) {
        onSuccess();
      }

      // Close the modal after showing success message
      setTimeout(() => {
        onClose();
      }, 1000); // Close after 1 second so user sees the success message

      // Don't redirect automatically - let the success notification be visible
      // Parent component or user can navigate as needed
    } catch (error) {
      console.error('Error creating subscription after Razorpay payment:', error);
      showError(`Failed to create subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle UPI payment
  const handleUpiPayment = async () => {
    if (isRazorpayCheckoutSuppressed()) {
      showError('UPI is not used while Dodo checkout is enabled. Use “Continue to Dodo checkout”.');
      return;
    }
    showError('UPI payments are only available via Razorpay. Please select Razorpay as your payment gateway.');
    return;
  };

  // Handle payment error
  const handlePaymentError = (error: string) => {
    showError(error || 'Payment failed. Please try again.');
    setIsProcessing(false);
  };

  const handleDodoCheckout = async () => {
    if (!checkoutData) {
      showError('Checkout data is missing. Close this window and open checkout again.');
      return;
    }
    if (!currentUser) {
      showError('You must be signed in to pay. Sign in and try again, or refresh the page.');
      return;
    }
    setIsProcessing(true);
    try {
      const returnOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      const { checkoutUrl } = await createDodoCheckoutSession({
        plan: checkoutData.plan,
        subscribers: checkoutData.subscribers,
        billingPeriod: checkoutData.billingPeriod,
        customerEmail: billingData.email || currentUser.email || '',
        customerName: `${billingData.firstName} ${billingData.lastName}`.trim(),
        returnOrigin,
      });
      window.location.assign(checkoutUrl);
    } catch (e) {
      console.error('[Dodo checkout]', e);
      showError(e instanceof Error ? e.message : 'Could not start Dodo checkout');
      setIsProcessing(false);
    }
  };

  // Handle step navigation
  const handleContinue = () => {
    if (currentStep === 'summary') {
      setCurrentStep('billing');
    } else if (currentStep === 'billing') {
      if (validateBillingInfo()) {
        const countryKey = userCountry || billingData.country;
        const gateways = getAvailablePaymentGateways(countryKey);
        if (isRazorpayCheckoutSuppressed()) {
          setSelectedGateway('dodo');
          setSelectedPaymentMethod(null);
        } else {
          if (gateways.length >= 1 && !selectedGateway) {
            setSelectedGateway(gateways[0] as 'razorpay' | 'stripe' | 'dodo');
          }
          if (gateways.length >= 1 && selectedGateway && !gateways.includes(selectedGateway)) {
            setSelectedGateway(gateways[0] as 'razorpay' | 'stripe' | 'dodo');
          }
        }
        setCurrentStep('payment');
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 'payment') {
      setCurrentStep('billing');
    } else if (currentStep === 'billing') {
      if (skipPlanSummary) {
        onClose();
      } else {
        setCurrentStep('summary');
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  if (preferShopifyBilling) {
    const handleContinueShopify = async () => {
      if (!checkoutData) {
        onClose();
        return;
      }
      setShopifyBillingBusy(true);
      try {
        await initiateShopifyBilling(checkoutData.plan, checkoutData.subscribers, checkoutData.billingPeriod);
      } catch (e) {
        console.error(e);
        showError(e instanceof Error ? e.message : 'Could not open Shopify billing.');
      } finally {
        setShopifyBillingBusy(false);
      }
    };

    return (
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900">Shopify billing</h2>
            <p className="mt-3 text-sm text-gray-600">
              App subscriptions installed from the Shopify App Store are billed through Shopify. Card checkout and
              third-party gateways are not used for this workspace.
            </p>
            {checkoutData ? (
              <button
                type="button"
                onClick={handleContinueShopify}
                disabled={shopifyBillingBusy}
                className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {shopifyBillingBusy ? 'Opening Shopify…' : 'Continue to Shopify to approve the charge'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!checkoutData) {
    return null;
  }

  const totalSteps = skipPlanSummary ? 2 : 3;
  const stepIndexLabel =
    skipPlanSummary
      ? currentStep === 'billing'
        ? '1'
        : '2'
      : currentStep === 'summary'
        ? '1'
        : currentStep === 'billing'
          ? '2'
          : '3';

  const progressSteps: { key: Step; label: string }[] = skipPlanSummary
    ? [
        { key: 'billing', label: 'Billing Info' },
        { key: 'payment', label: 'Payment' },
      ]
    : [
        { key: 'summary', label: 'Plan Summary' },
        { key: 'billing', label: 'Billing Info' },
        { key: 'payment', label: 'Payment' },
      ];

  const stepLinearIndex = (s: Step): number => {
    if (skipPlanSummary) {
      if (s === 'billing') return 0;
      if (s === 'payment') return 1;
      return -1;
    }
    if (s === 'summary') return 0;
    if (s === 'billing') return 1;
    if (s === 'payment') return 2;
    return -1;
  };
  const currentLinearIndex = stepLinearIndex(currentStep);

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" style={{ fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200" style={{ fontFamily: 'Arial, sans-serif' }}>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Complete Your Subscription</h2>
              <p className="text-sm text-gray-600 mt-1">
                Step {currentStep === 'summary' ? '1' : currentStep === 'billing' ? '2' : '3'} of 3
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center justify-between">
              {progressSteps.map(({ key: step, label }, index) => {
                const isActive = currentStep === step;
                const isCompleted =
                  (step === 'summary' && currentStep !== 'summary') ||
                  (step === 'billing' && currentStep === 'payment');

                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex items-center flex-1">
                      <div
                        className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-colors ${isActive || isCompleted
                          ? 'bg-gray-900 border-gray-900 text-white'
                          : 'bg-white border-gray-300 text-gray-400'
                          }`}
                      >
                        {isCompleted ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`ml-2 text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                        {label}
                      </span>
                    </div>
                    {index < progressSteps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 ${
                          currentLinearIndex > index ? 'bg-gray-900' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Step 1: Plan Summary */}
            {currentStep === 'summary' && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Plan Summary</h3>

                  <div className="bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 capitalize">{checkoutData.plan} Plan</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {checkoutData.subscribers.toLocaleString()} contacts
                        </p>
                        <p className="text-sm text-gray-600">
                          {checkoutData.plan === 'business' ? 'Unlimited' : getProMonthlyEmailLimit(checkoutData.subscribers).toLocaleString()} emails /mo*
                        </p>
                        <p className="text-sm text-gray-600">
                          Billed {checkoutData.billingPeriod === 'annual' ? 'yearly' : 'monthly'}
                        </p>
                      </div>
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(
                            checkoutData.billingPeriod === 'annual' ? annualPrice / 12 : monthlyPrice,
                            displayCurrency
                          )}/mo
                        </div>
                    </div>
                  </div>

                  {/* Subscription Period */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Subscription Period</h5>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Start Date:</span>
                        <p className="font-medium text-gray-900">{formatDate(subscriptionStartDate)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">End Date:</span>
                        <p className="font-medium text-gray-900">{formatDate(subscriptionEndDate)}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Billing Cycle:</span>
                        <p className="font-medium text-gray-900">{billingCycle}</p>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">What's Included</h5>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{checkoutData.plan === 'pro' ? '3' : 'Unlimited'} Workspaces</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Up to {checkoutData.subscribers.toLocaleString()} Contacts</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                         <span className="text-gray-700">{checkoutData.plan === 'business' ? 'Unlimited' : getProMonthlyEmailLimit(checkoutData.subscribers).toLocaleString()}* Emails /mo</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Advanced Automations</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Custom Domain Included</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Priority Support</span>
                      </li>
                      <li className="mt-2 pt-2 border-t border-gray-100">
                        <span className="text-[10px] text-gray-400 italic">*Subject to fair use; Pro limits follow your contact tier</span>
                      </li>
                    </ul>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Pricing Breakdown</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="text-gray-900">{formatCurrency(subtotalBeforeCredits, displayCurrency)}</span>
                      </div>
                      {creditsToApply > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Referral Credits Applied:</span>
                          <span>-{formatCurrency(creditsToApply, displayCurrency)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal After Credits:</span>
                        <span className="text-gray-900">{formatCurrency(subtotal, displayCurrency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Tax ({userCountry === 'India' || userCountry === 'IN' ? '18% GST' : '8%'}):
                        </span>
                        <span className="text-gray-900">{formatCurrency(tax, displayCurrency)}</span>
                      </div>
                      {checkoutData.billingPeriod === 'annual' && savings > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Annual Savings:</span>
                          <span>-{formatCurrency(savings, displayCurrency)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-gray-900">{formatCurrency(grandTotal, displayCurrency)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Billing Information */}
            {currentStep === 'billing' && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing Information</h3>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.firstName}
                          onChange={(e) => handleBillingChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.lastName}
                          onChange={(e) => handleBillingChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={billingData.email}
                        onChange={(e) => handleBillingChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={billingData.companyName}
                        onChange={(e) => handleBillingChange('companyName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={billingData.addressLine1}
                        onChange={(e) => handleBillingChange('addressLine1', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={billingData.addressLine2}
                        onChange={(e) => handleBillingChange('addressLine2', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.city}
                          onChange={(e) => handleBillingChange('city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.state}
                          onChange={(e) => handleBillingChange('state', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.zipCode}
                          onChange={(e) => handleBillingChange('zipCode', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={billingData.country}
                          onChange={(e) => handleBillingChange('country', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                          required
                        >
                          <option value="United States">United States (USD)</option>
                          <option value="India">India (INR)</option>
                          <option value="United Kingdom">United Kingdom (GBP)</option>
                          <option value="Canada">Canada (CAD)</option>
                          <option value="Australia">Australia (AUD)</option>
                          <option value="Germany">Germany (EUR)</option>
                          <option value="France">France (EUR)</option>
                          <option value="Singapore">Singapore (SGD)</option>
                          <option value="United Arab Emirates">United Arab Emirates (AED)</option>
                          <option value="Japan">Japan (JPY)</option>
                          <option value="Other">Other</option>
                        </select>
                        {billingData.country && billingData.country !== 'Other' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Currency: {displayCurrency} • Gateway: {checkoutGatewayLabel}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={billingData.phoneNumber}
                          onChange={(e) => handleBillingChange('phoneNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Payment */}
            {currentStep === 'payment' && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Method</h3>

                  {loadingCustomer && (
                    <div className="flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                      <span className="ml-2 text-gray-600">Initializing payment...</span>
                    </div>
                  )}

                  {!loadingCustomer && (
                    <div>
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-gray-600">Order Total</p>
                          {userCurrency !== 'USD' && (
                            <span className="text-xs text-gray-500">
                              ({formatCurrency(totalAmountUSD, 'USD')} USD)
                            </span>
                          )}
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(grandTotal, displayCurrency)}
                        </p>
                        {userCurrency && userCurrency !== checkoutData?.currency && (
                          <p className="text-xs text-gray-500 mt-1">
                            Amount displayed in {getCurrencyInfo(displayCurrency)?.name || displayCurrency}
                          </p>
                        )}
                      </div>

                      {/* Payment Gateway Selection */}
                      {!isRazorpayCheckoutSuppressed() &&
                        !isDetectingCountry &&
                        getAvailablePaymentGateways(userCountry || billingData.country).length > 1 && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Gateway
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            {!isRazorpayCheckoutSuppressed() &&
                              getAvailablePaymentGateways(userCountry || billingData.country).includes('razorpay') && (
                              <button
                                type="button"
                                onClick={() => setSelectedGateway('razorpay')}
                                className={`p-3 border-2 rounded-lg transition-all text-left ${selectedGateway === 'razorpay'
                                  ? 'border-gray-900 bg-gray-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                              >
                                <div className="font-medium text-gray-900">Razorpay</div>
                                <p className="text-xs text-gray-500 mt-1">Best for UPI & India</p>
                              </button>
                            )}
                            {getAvailablePaymentGateways(userCountry || billingData.country).includes('dodo') && (
                              <button
                                type="button"
                                onClick={() => setSelectedGateway('dodo')}
                                className={`p-3 border-2 rounded-lg transition-all text-left ${selectedGateway === 'dodo'
                                  ? 'border-gray-900 bg-gray-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                              >
                                <div className="font-medium text-gray-900">Dodo Payments</div>
                                <p className="text-xs text-gray-500 mt-1">India and international</p>
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedGateway === 'dodo' || isRazorpayCheckoutSuppressed() ? (
                        <div className="space-y-3">
                          {availableCredits > 0 && (
                            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900">
                              Referral credits are not applied on Dodo hosted checkout (catalog pricing).
                            </div>
                          )}
                          <div className="border border-gray-200 rounded-lg p-6 bg-white">
                            <p className="text-sm text-gray-700 mb-4">
                              You will be redirected to Dodo Payments to enter your card securely. After payment, you will return here and your plan will activate automatically.
                            </p>
                            <button
                              type="button"
                              onClick={handleDodoCheckout}
                              disabled={isProcessing}
                              className="w-full py-3 px-4 rounded-md bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? 'Redirecting…' : 'Continue to Dodo checkout'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                      {/* Payment Method Selection */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Payment Method <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod('card')}
                            className={`p-4 border-2 rounded-lg transition-all text-left ${selectedPaymentMethod === 'card'
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === 'card'
                                ? 'border-gray-900 bg-gray-900'
                                : 'border-gray-300'
                                }`}>
                                {selectedPaymentMethod === 'card' && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <svg className={`w-6 h-6 ${selectedPaymentMethod === 'card' ? 'text-gray-900' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              <span className={`font-medium ${selectedPaymentMethod === 'card' ? 'text-gray-900' : 'text-gray-900'}`}>
                                Credit/Debit Card
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">Visa, Mastercard, Amex</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod('upi')}
                            className={`p-4 border-2 rounded-lg transition-all text-left ${selectedPaymentMethod === 'upi'
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === 'upi'
                                ? 'border-gray-900 bg-gray-900'
                                : 'border-gray-300'
                                }`}>
                                {selectedPaymentMethod === 'upi' && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <svg className={`w-6 h-6 ${selectedPaymentMethod === 'upi' ? 'text-gray-900' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <span className={`font-medium ${selectedPaymentMethod === 'upi' ? 'text-gray-900' : 'text-gray-900'}`}>
                                UPI
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">Google Pay, PhonePe, Paytm</p>
                          </button>
                        </div>
                      </div>

                      {/* Card Payment Form - Stripe */}
                      {selectedPaymentMethod === 'card' && selectedGateway === 'stripe' && (
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <div className="flex items-center gap-2 mb-4">
                            <h4 className="text-sm font-semibold text-gray-900">Card Details</h4>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Stripe</span>
                          </div>
                          <PaymentForm
                            amount={grandTotal}
                            currency={displayCurrency.toLowerCase()}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                            isSubscription={true}
                            customerId={customerId || undefined}
                            description={`${checkoutData.plan === 'pro' ? 'Pro' : 'Business'} Plan - ${checkoutData.subscribers.toLocaleString()} contacts`}
                          />
                        </div>
                      )}

                      {/* Card Payment Form - Razorpay (hidden when Dodo checkout is enabled for this build) */}
                      {selectedPaymentMethod === 'card' &&
                        selectedGateway === 'razorpay' &&
                        !isRazorpayCheckoutSuppressed() && (
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <div className="flex items-center gap-2 mb-4">
                            <h4 className="text-sm font-semibold text-gray-900">Card Details</h4>
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">Razorpay</span>
                          </div>
                          <RazorpayPaymentForm
                            planSelection={{ plan: checkoutData.plan === 'business' ? 'business' : 'pro', subscribers: checkoutData.subscribers, billingPeriod: checkoutData.billingPeriod === 'annual' ? 'annual' : 'monthly' }}
                            amount={grandTotal}
                            currency={displayCurrency}
                            customerName={`${billingData.firstName} ${billingData.lastName}`.trim() || undefined}
                            customerEmail={billingData.email || undefined}
                            customerContact={billingData.phoneNumber || undefined}
                            description={`${checkoutData.plan === 'pro' ? 'Pro' : 'Business'} Plan - ${checkoutData.subscribers.toLocaleString()} contacts${creditsToApply > 0 ? ` (${formatCurrency(creditsToApply, displayCurrency)} credits applied)` : ''}`}
                            onSuccess={handleRazorpaySuccess}
                            onError={handlePaymentError}
                            paymentMethod="card"
                          />
                        </div>
                      )}

                      {/* UPI Payment Form - Razorpay (hidden when Dodo checkout is enabled for this build) */}
                      {selectedPaymentMethod === 'upi' &&
                        selectedGateway === 'razorpay' &&
                        !isRazorpayCheckoutSuppressed() && (
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <div className="flex items-center gap-2 mb-4">
                            <h4 className="text-sm font-semibold text-gray-900">UPI Payment</h4>
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">Razorpay</span>
                          </div>
                          <RazorpayPaymentForm
                            planSelection={{ plan: checkoutData.plan === 'business' ? 'business' : 'pro', subscribers: checkoutData.subscribers, billingPeriod: checkoutData.billingPeriod === 'annual' ? 'annual' : 'monthly' }}
                            amount={grandTotal}
                            currency={displayCurrency}
                            customerName={`${billingData.firstName} ${billingData.lastName}`.trim() || undefined}
                            customerEmail={billingData.email || undefined}
                            customerContact={billingData.phoneNumber || undefined}
                            description={`${checkoutData.plan === 'pro' ? 'Pro' : 'Business'} Plan - ${checkoutData.subscribers.toLocaleString()} contacts`}
                            onSuccess={handleRazorpaySuccess}
                            onError={handlePaymentError}
                            paymentMethod="upi"
                          />
                        </div>
                      )}

                      {/* UPI Payment Form - Stripe (Not Supported) */}
                      {selectedPaymentMethod === 'upi' && selectedGateway === 'stripe' && (
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <div className="flex items-center gap-2 mb-4">
                            <h4 className="text-sm font-semibold text-gray-900">UPI Payment</h4>
                            <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded">Not Available</span>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <div className="text-sm text-amber-800">
                                <p className="font-medium mb-1">UPI is not available with Stripe</p>
                                <p className="text-xs">
                                  UPI payments are supported only via Razorpay. Please go back and select <strong>Razorpay</strong> as your payment gateway to pay with UPI, or choose <strong>Card</strong> payment with Stripe.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Payment Method Not Selected (hidden while only Dodo checkout is offered) */}
                  {!selectedPaymentMethod &&
                    selectedGateway !== 'dodo' &&
                    !isRazorpayCheckoutSuppressed() && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <p className="text-gray-500 text-sm">Please select a payment method to continue</p>
                    </div>
                  )}

                  {!selectedGateway && (
                    <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                      <p className="text-sm text-red-800 font-medium mb-1">
                        No payment gateway is available in this build
                      </p>
                      <p className="text-xs text-red-700 leading-relaxed">
                        {isRazorpayCheckoutSuppressed()
                          ? 'Try going back one step, then continue again. If this persists, refresh the page.'
                          : 'This screen needs a payment provider that is not included in the copy of the app you are running. Use a fresh deploy of the current release, or ask your team which build you should use.'}
                      </p>
                    </div>
                  )}

                  {isProcessing && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-800">Processing your subscription...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between gap-4">
              <button
                onClick={
                  currentStep === 'summary' || (skipPlanSummary && currentStep === 'billing')
                    ? onClose
                    : handleBack
                }
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {currentStep === 'summary' || (skipPlanSummary && currentStep === 'billing')
                  ? 'Cancel'
                  : 'Back'}
              </button>
              {currentStep !== 'payment' && (
                <button
                  onClick={handleContinue}
                  disabled={currentStep === 'billing' && !isBillingInfoComplete()}
                  className={`px-6 py-2 rounded-md transition-colors ${currentStep === 'billing' && !isBillingInfoComplete()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                >
                  {currentStep === 'summary' ? 'Continue to Billing Information' : 'Continue to Payment'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default CheckoutModal;

